import type {
  RazorpayCheckoutPrefill,
  RazorpayCheckoutSuccess,
} from '../models/razorpay-checkout.model';

const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

interface RazorpayCheckoutOptions {
  key: string;
  order_id: string;
  currency: string;
  name: string;
  description: string;
  prefill?: RazorpayCheckoutPrefill;
  theme?: { color?: string };
  handler: (response: RazorpayCheckoutSuccess) => void;
  modal?: {
    ondismiss?: () => void;
  };
}

interface RazorpayCheckoutInstance {
  open(): void;
  on(event: 'payment.failed', handler: (response: unknown) => void): void;
}

type GlobalWithRazorpay = typeof globalThis & {
  Razorpay?: new (options: RazorpayCheckoutOptions) => RazorpayCheckoutInstance;
};

function globalWithRazorpay(): GlobalWithRazorpay {
  return globalThis as GlobalWithRazorpay;
}

let razorpayScriptPromise: Promise<void> | null = null;

function loadRazorpayScript(): Promise<void> {
  if (typeof globalWithRazorpay().Razorpay === 'function') {
    return Promise.resolve();
  }
  if (razorpayScriptPromise) {
    return razorpayScriptPromise;
  }

  razorpayScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${RAZORPAY_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load Razorpay checkout script')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load Razorpay checkout script'));
    document.body.appendChild(script);
  });

  return razorpayScriptPromise;
}

export type RazorpayCheckoutInitiate = {
  razorpayKeyId: string;
  razorpayOrderId: string;
  currency: string;
};

export type OpenRazorpayCheckoutInput = {
  initiate: RazorpayCheckoutInitiate;
  merchantName: string;
  description: string;
  prefill?: RazorpayCheckoutPrefill;
  /** Razorpay modal accent color (defaults to CP booking blue). */
  themeColor?: string;
};

export async function openRazorpayCheckout(
  input: OpenRazorpayCheckoutInput,
): Promise<RazorpayCheckoutSuccess> {
  await loadRazorpayScript();
  const Razorpay = globalWithRazorpay().Razorpay;
  if (typeof Razorpay !== 'function') {
    throw new TypeError('Razorpay checkout is unavailable');
  }

  const themeColor = input.themeColor ?? '#2563eb';

  return new Promise<RazorpayCheckoutSuccess>((resolve, reject) => {
    const checkout = new Razorpay({
      key: input.initiate.razorpayKeyId,
      order_id: input.initiate.razorpayOrderId,
      currency: input.initiate.currency || 'INR',
      name: input.merchantName,
      description: input.description,
      prefill: input.prefill,
      theme: { color: themeColor },
      handler: response => resolve(response),
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled')),
      },
    });

    checkout.on('payment.failed', () => {
      reject(new Error('Payment failed. Please try again.'));
    });

    checkout.open();
  });
}
